import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import usePageMeta from "@/hooks/usePageMeta";
import Reveal from "@/components/Reveal";
import { BUSINESS, IMAGES } from "@/data/content";

const DETAILS = {
  "ceramic-coating": {
    kicker: "Paint protection",
    title: ["Ceramic", "Coating"],
    accentIndex: 1,
    meta: "Ceramic Coating — Luxpress Detailing, South Melbourne",
    desc: "Paint correction and premium ceramic coatings in South Melbourne. Wheel coatings and interior fabric protection. Quoted per vehicle.",
    intro: "A ceramic coating is only as good as the paint underneath it. Every coating job here starts with correction — machine polishing until the surface is right — then the coating locks that finish in.",
    image: "ceramic",
    imageAlt: "Water beading on a ceramic-coated dark paint surface",
    includes: [
      "Full paint correction before any coating is applied",
      "Premium ceramic coatings, matched to the car and its use",
      "Wheel coatings — faces and barrels",
      "Interior fabric protection",
      "pH-neutral chemicals and premium products throughout",
    ],
    body: "Coated paint behaves differently. Water beads and sheets off instead of sitting in the pores. Road film washes away instead of bonding. The car stays cleaner between washes, and every wash is safer because less contact is needed. We quote ceramic work per vehicle, in person or from photos, because condition and coverage decide the price — not a poster.",
    serviceId: "ceramic-coating",
  },
  "ppf-vinyl-wraps": {
    kicker: "Film & wrap",
    title: ["Paint Protection", "Film & Wraps"],
    accentIndex: 1,
    meta: "Paint Protection Film & Vinyl Wraps — Luxpress Detailing",
    desc: "Consultation-based paint protection film and vinyl wraps in South Melbourne. Measured, cut and fitted for your car.",
    intro: "Film is the only protection that stops a stone chip. PPF takes the impact so the paint doesn't have to — and a wrap changes the car entirely without touching the factory finish.",
    image: "ppf",
    imageAlt: "Detailer polishing a modern black vehicle, protection film work in progress",
    includes: [
      "Consultation first — coverage, finish and budget decided together",
      "High-impact zones: bonnet, bumper, guards, mirrors, sills",
      "Full-car film and colour-change wraps",
      "Edges wrapped and tucked, not trimmed on the car",
      "Film over corrected paint, never over defects",
    ],
    body: "Every film job starts with a conversation about how the car is used. A daily-driven SUV needs different coverage than a weekend car. We measure, cut and fit for your vehicle specifically, and we correct the paint first — film seals in whatever is underneath, so what's underneath has to be right.",
    serviceId: "ppf-vinyl-wraps",
  },
};

const ServiceDetail = ({ slug }) => {
  const d = DETAILS[slug];
  const flyer = slug === "ceramic-coating" ? "/assets/flyer-ceramic.jpg" : "/assets/flyer-ppf.jpg";
  usePageMeta(d.meta, d.desc);
  return (
    <div data-testid={`service-detail-${slug}`}>
      <section className="mx-auto max-w-7xl px-5 pt-36 sm:px-8 sm:pt-44">
        <Reveal>
          <p className="kicker">{d.kicker}</p>
          <h1 className="mt-4 font-display text-4xl font-black uppercase leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
            {d.title.map((line, i) => (
              <span key={line} className={`block ${i === d.accentIndex ? "text-mint" : ""}`}>{line}</span>
            ))}
          </h1>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-chrome sm:text-lg">{d.intro}</p>
        </Reveal>
        <Reveal delay={0.15} className="mt-14">
          <img
            src={IMAGES[d.image]}
            alt={d.imageAlt}
            className="aspect-[16/8] w-full border border-teal-line object-cover sm:aspect-[21/9]"
          />
        </Reveal>
      </section>

      <section className="mx-auto grid max-w-7xl gap-14 px-5 py-24 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <p className="kicker">What's included</p>
          <ul className="mt-8 space-y-5">
            {d.includes.map((item) => (
              <li key={item} className="flex items-start gap-4 border-b border-teal-line pb-5">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-mint" strokeWidth={2.5} aria-hidden="true" />
                <span className="text-sm leading-relaxed text-white/90 sm:text-base">{item}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="kicker">Why it's quoted</p>
          <p className="mt-8 text-base leading-relaxed text-chrome">{d.body}</p>
          <div className="mt-10 border border-teal-line bg-panel p-8">
            <p className="spec-label">On quote — per vehicle</p>
            <p className="mt-3 font-display text-2xl font-bold uppercase tracking-tight">
              Tell us the car.<br />We'll tell you the number.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link to={`/contact?service=${d.serviceId}`} data-testid="service-detail-quote-cta" className="btn-primary">
                Request a quote <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <a href={BUSINESS.phoneHref} data-testid="service-detail-call-cta" className="btn-ghost">{BUSINESS.phoneDisplay}</a>
            </div>
          </div>
          <Dialog>
            <DialogTrigger asChild>
              <button
                data-testid="service-flyer-btn"
                className="group mt-6 flex w-full items-center gap-5 border border-teal-line bg-panel p-4 text-left transition-colors duration-300 hover:border-mint/50"
              >
                <img src={flyer} alt={`${d.title.join(" ")} flyer`} loading="lazy" className="aspect-[2/3] w-20 shrink-0 border border-teal-line object-cover" />
                <span>
                  <span className="spec-label block">In print</span>
                  <span className="mt-1 block font-display text-sm font-bold uppercase tracking-tight text-white">The {d.title.join(" ")} flyer</span>
                  <span className="mt-1 block font-mono text-[11px] uppercase tracking-[0.18em] text-mint">Tap to read full size</span>
                </span>
              </button>
            </DialogTrigger>
            <DialogContent className="max-h-[92vh] w-[94vw] max-w-md overflow-y-auto border-teal-line bg-black p-2 sm:max-w-lg" data-testid="service-flyer-dialog">
              <DialogTitle className="sr-only">{d.title.join(" ")} flyer</DialogTitle>
              <img src={flyer} alt={`${d.title.join(" ")} flyer`} className="w-full" />
            </DialogContent>
          </Dialog>
        </Reveal>
      </section>
    </div>
  );
};

export default ServiceDetail;
